import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Target, Star, TrendingUp, TrendingDown, Users, ChevronRight, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, size = 'sm' }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
  const sizeClass = size === 'lg' ? 'h-5 w-5' : 'h-3 w-3';
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} className={`${sizeClass} fill-yellow-400 text-yellow-400`} />
      ))}
      {hasHalf && <Star className={`${sizeClass} fill-yellow-400/50 text-yellow-400`} />}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`empty-${i}`} className={`${sizeClass} text-gray-300`} />
      ))}
    </div>
  );
};

const ScoreBar = ({ label, score, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium">{score}%</span>
    </div>
    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
      <div 
        className={`h-full ${color} transition-all duration-500`}
        style={{ width: `${score}%` }}
      />
    </div>
  </div>
);

const KPIWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    loadKPIData();
  }, []);
  
  const loadKPIData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kpi/dashboard-summary`);
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error loading KPI data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async (format) => {
    try {
      const response = await fetch(`${API_URL}/api/kpi/export/summary?format=${format}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `KPI_Summary_${new Date().getFullYear()}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };
  
  if (loading) {
    return (
      <Card className="animate-pulse" data-testid="kpi-widget-loading">
        <CardContent className="p-6">
          <div className="h-32 bg-gray-200 rounded"></div>
        </CardContent>
      </Card>
    );
  }
  
  if (!data || data.total_reviews === 0) {
    return (
      <Card data-testid="kpi-widget-empty">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5 text-indigo-600" />
            KPI Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-gray-500">
            <Target className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No KPI reviews yet</p>
            <Button 
              variant="link" 
              className="text-indigo-600 mt-2"
              onClick={() => navigate('/kpi')}
            >
              Go to KPI Management
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const categoryColors = {
    Performance: 'bg-indigo-500',
    Attendance: 'bg-green-500',
    Quality: 'bg-blue-500',
    Behavior: 'bg-amber-500'
  };
  
  return (
    <Card className="overflow-hidden" data-testid="kpi-widget">
      <CardHeader className="pb-2 bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5 text-indigo-600" />
            KPI Performance Overview
          </CardTitle>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2 text-xs"
              onClick={() => handleExport('pdf')}
            >
              <Download className="h-3 w-3 mr-1" />
              PDF
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2 text-xs"
              onClick={() => handleExport('excel')}
            >
              <Download className="h-3 w-3 mr-1" />
              Excel
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-indigo-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-indigo-700">{data.average_score}%</p>
            <p className="text-xs text-indigo-600">Avg Score</p>
            <StarRating rating={data.average_stars} />
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{data.approved_reviews}</p>
            <p className="text-xs text-green-600">Completed</p>
            <p className="text-xs text-gray-500">of {data.total_reviews} reviews</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-purple-700">{data.kpi_enabled_employees}</p>
            <p className="text-xs text-purple-600">Employees</p>
            <p className="text-xs text-gray-500">KPI enabled</p>
          </div>
        </div>
        
        {/* Category Performance */}
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Category Performance</p>
          <div className="space-y-2">
            {data.category_averages?.map(cat => (
              <ScoreBar 
                key={cat.category}
                label={cat.category}
                score={cat.avg_score}
                color={categoryColors[cat.category]}
              />
            ))}
          </div>
        </div>
        
        {/* Top Performers */}
        {data.top_performers?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-green-500" />
              Top Performers
            </p>
            <div className="space-y-1">
              {data.top_performers.slice(0, 3).map((emp, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm p-2 rounded bg-green-50">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <span className="font-medium truncate max-w-[120px]">{emp.employee_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-700 font-bold">{emp.score}%</span>
                    <StarRating rating={emp.star_rating} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Needs Improvement */}
        {data.needs_improvement?.length > 0 && data.needs_improvement[0]?.score < 60 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase flex items-center gap-1">
              <TrendingDown className="h-3 w-3 text-amber-500" />
              Needs Attention
            </p>
            <div className="space-y-1">
              {data.needs_improvement.filter(e => e.score < 60).slice(0, 2).map((emp, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm p-2 rounded bg-amber-50">
                  <span className="font-medium truncate max-w-[150px]">{emp.employee_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-amber-700 font-bold">{emp.score}%</span>
                    <StarRating rating={emp.star_rating} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Department Breakdown */}
        {data.department_breakdown?.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase">By Department</p>
            <div className="flex flex-wrap gap-1">
              {data.department_breakdown.slice(0, 5).map((dept, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className="text-xs"
                  style={{
                    borderColor: dept.avg_score >= 70 ? '#22c55e' : dept.avg_score >= 50 ? '#eab308' : '#ef4444',
                    color: dept.avg_score >= 70 ? '#16a34a' : dept.avg_score >= 50 ? '#ca8a04' : '#dc2626'
                  }}
                >
                  {dept.department}: {dept.avg_score}%
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* View All Button */}
        <Button 
          variant="outline" 
          className="w-full mt-2 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
          onClick={() => navigate('/kpi')}
          data-testid="kpi-widget-view-all"
        >
          <Target className="h-4 w-4 mr-2" />
          View KPI Management
          <ChevronRight className="h-4 w-4 ml-auto" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default KPIWidget;
